import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useUpdateEffect } from "react-use";
import { useRecoilValue } from "recoil";
import { quizFocusIndex } from "../../states/atoms/quizState";
import {
  Button,
  ButtonGroup,
  ButtonGroupItem,
  GhostButton,
  IconButton,
  LinkButton,
  OptionSelect,
  OptionText,
  OptionTextArea,
  TextArea,
  TextInput,
} from "../common/Forms";
import Icon from "../common/Icon";
import Message from "../common/Message";
import Quiz from "./Quiz";

const AddQuizCard = (props) => {
  const focusIndex = useRecoilValue(quizFocusIndex);
  const [q, setQ] = useState([]);
  const [qType, setType] = useState("text");
  const [qRequired, setRequired] = useState(true);
  const [option, setOption] = useState("");
  const [options, setOptions] = useState([{ content: "" }]);
  const [answers, setAnswers] = useState([]);
  const [editable, setEditable] = useState(props.editable);
  const [error, setError] = useState({ q: null, a: null });

  useEffect(() => {
    setQ(props.q.content);
    setType(props.q.type);
    setRequired(props.q.required);
    setAnswers(props.answers);
    if (props.q.type === "text" || props.q.type === "textarea") {
      setOption(props.option.content);
      setOptions([{ content: "" }]);
    } else {
      setOption("");
      setOptions(props.option.content);
    }
  }, [props.q, props.q.required, props.q.type, props.option, props.answers]);

  useUpdateEffect(() => {
    if (focusIndex === props.n - 1 && editable && !props.editable) {
      const val = validate();
      if (val) {
        props.onAdd(val, props.n);
      }
    }
    setEditable(props.editable);
  }, [props.editable]);

  useUpdateEffect(() => {
    if (editable) {
      const val = validate();
      if (val) {
        props.onAdd(val, props.n, true);
      }
    }
  }, [focusIndex]);

  useUpdateEffect(() => {
    validate();
  }, [answers]);

  const handleQuestion = (e) => {
    if (e.target.value) {
      setError({ q: null });
    } else {
      setError({ q: "질문을 입력하세요." });
    }
    setQ(e.target.value.split(/\r|\r\n|\n/));
  };

  const handleType = (e) => {
    setError({ ...error, a: null });
    setAnswers([]);
    if (qType === e.target.id && qType === "checkbox") {
      setType("radio");
    } else {
      if (
        ["checkbox", "radio"].includes(qType) &&
        ["text", "textarea"].includes(e.target.id)
      ) {
        setOption("");
        setOptions([{ content: "" }]);
      }
      setType(e.target.id);
    }
  };

  const handleRequired = (e) => {
    setRequired(!qRequired);
  };

  const handleOption = (e) => {
    if (qType === "text" || qType === "textarea") {
      setOption(e.target.value);
    }
  };

  const handleChangeOption = (newOne) => {
    /* const newOne = [...options];
      const obj = newOne[idx];
      if (bSub) {
        if (typeof obj.content === "string") {
          const content = obj.content;
          newOne.splice(idx, 1, { content: { type: "text", content } });
        } else {
          newOne.splice(idx, 1, { content: obj.content.content });
        }
      } else {
        if (typeof obj.content === "string") {
          newOne.splice(idx, 1, { content: e.target.value });
        } else {
          newOne.splice(idx, 1, {
            content: { type: "text", content: e.target.value },
          });
        }
      } */
    setOptions(newOne);
  };

  const handleAddOptions = (newOne) => {
    setOptions(newOne);
  };

  const handleRemoveOption = (newOne) => {
    setOptions(newOne);
  };

  const handleChangeAnswer = (idx) => (e) => {
    const v = e.target.value.trimStart().trimEnd();
    if (v) {
      const nAnswers = [...answers];
      nAnswers.splice(idx, 1, v);
      setAnswers(nAnswers);
    }
    // setAnswers([...new Set(answers)]);
  };

  const handleAddAnswers = () => {
    setAnswers([...answers, ""]);
  };

  const handleRemoveAnswer = (idx) => () => {
    const nAnswers = [...answers];
    nAnswers.splice(idx, 1);
    setAnswers(nAnswers);
  };

  const handleClick = (e) => {
    if (!editable || (editable && e.target.id === props.id)) {
      props.onClick(props.n - 1);
    }
  };

  const validate = () => {
    setError({ q: null, a: null });
    if (!q || q.length === 0) {
      setError({ q: "질문을 입력하세요." });
      return false;
    }
    if (qType === "radio" || qType === "checkbox") {
      if (options.length === 0) {
        setError({ a: "보기를 입력하세요." });
        return false;
      }
      if (answers.length === 0) {
        setError({ a: "정답을 선택하세요." });
        return false;
      }
    } else if (!option) {
      setError({ a: "보기를 입력하세요." });
      return false;
    } else if (
      answers.length === 0 ||
      answers.join("").replace(/\s/g, "").length === 0
    ) {
      setError({ a: "정답을 입력하세요." });
      return false;
    }

    const id = uuidv4();
    return {
      id,
      key: id,
      q: {
        content: q,
        required: qRequired,
        type: qType,
      },
      option: {
        content:
          ["text", "textarea", "rating"].indexOf(qType) > -1 ? option : options,
      },
      answers,
    };
  };

  const handleAdd = () => {
    const val = validate();
    if (val) {
      props.onAdd(val);
      props.onClick(props.n);
    }
  };

  const handleUp = () => {
    const val = validate();
    if (val) {
      props.onMove(val, -1);
    }
  };

  const handleDown = () => {
    const val = validate();
    if (val) {
      props.onMove(val, 1);
    }
  };

  const handleAnswer = (newOne) => {
    setAnswers(newOne);
  };

  return (
    <div
      id={props.id}
      className="flex flex-col items-center gap-4 px-4 pb-4 bg-white w-full"
      onClick={handleClick}
    >
      {!editable && (
        <Quiz
          {...{
            answers,
            n: props.n,
            option,
            options,
            q,
            readOnly: true,
            type: qType,
          }}
        />
      )}
      {editable && (
        <div
          className={`flex flex-col pb-4 px-2 rounded-2xl mx-4 bg-zinc-200 w-full`}
        >
          <div className="inline-flex self-end p-2 gap-2 opacity-70">
            <IconButton disabled={props.isLast} onClick={handleDown}>
              <Icon name="arrow-down" size="sm" />
            </IconButton>
            <IconButton disabled={props.isFirst} onClick={handleUp}>
              <Icon name="arrow-up" size="sm" />
            </IconButton>
            <IconButton className="ml-2" onClick={props.onRemove}>
              <Icon name="trash" size="sm" className="text-error" />
            </IconButton>
          </div>
          <div className="inline-flex gap-1 md:gap-2 w-full">
            <p className="font-bold">{props.n}.</p>
            <div className="flex flex-col gap-4 w-full">
              <TextArea
                id="qText"
                placeholder="질문을 입력하세요."
                onChange={handleQuestion}
                value={q}
                readOnly={!editable}
                className="flex-auto p-2 shadow-sm text-sm"
              />
              {!!error.q && <Message type="error">{error.q}</Message>}
              <div className="inline-flex flex-wrap gap-4">
                <ButtonGroup label="답변">
                  {[
                    { text: "단답형" },
                    { textarea: "서술형" },
                    { radio: "선택형" },
                  ].map((item) => {
                    const [k, v] = Object.entries(item)[0];
                    return (
                      <ButtonGroupItem
                        {...{
                          active:
                            k === qType ||
                            (qType === "checkbox" && k === "radio"),
                          disabled: !editable,
                          id: k,
                          key: k,
                          onClick: handleType,
                        }}
                      >
                        {v}
                      </ButtonGroupItem>
                    );
                  })}
                </ButtonGroup>
                <div className="inline-flex gap-4">
                  <div className="inline-flex gap-1 md:gap-2 items-center">
                    <p className="text-sm font-bold">필수</p>
                    <div className="form-control w-16">
                      <label className="cursor-pointer label">
                        <input
                          className="toggle toggle-accent"
                          type="checkbox"
                          checked={qRequired}
                          onChange={handleRequired}
                          readOnly={!editable}
                        />
                      </label>
                    </div>
                  </div>
                  {(qType === "radio" || qType === "checkbox") && (
                    <div className="inline-flex gap-1 md:gap-2 items-center">
                      <p className="text-sm font-bold">복수</p>
                      <div className="form-control w-16">
                        <label className="cursor-pointer label">
                          <input
                            id="checkbox"
                            type="checkbox"
                            className="toggle toggle-accent"
                            onChange={handleType}
                            checked={qType === "checkbox"}
                            readOnly={!editable}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {qType === "text" && (
                <>
                  <OptionText
                    value={option}
                    onChange={handleOption}
                    readOnly={!editable}
                  />
                  <div
                    className={`inline-flex gap-1 md:gap-2 items-${
                      answers.length ? "baseline" : "center"
                    }`}
                  >
                    <p className="text-sm font-bold">정답</p>
                    <div className="flex flex-col w-full">
                      <div className="flex flex-wrap w-full">
                        {answers.map((v, i) => (
                          <div
                            className="inline-flex w-full gap-1 md:gap-2 items-center"
                            key={`q${props.n}_answer_${i}`}
                          >
                            <TextInput
                              className="w-full p-2"
                              placeholder="정답을 입력하세요"
                              onBlur={handleChangeAnswer(i)}
                              defaultValue={v}
                            />
                            <GhostButton onClick={handleRemoveAnswer(i)}>
                              <Icon name="x-circle" className="text-zinc-600" />
                            </GhostButton>
                          </div>
                        ))}
                      </div>
                      <LinkButton onClick={handleAddAnswers}>
                        <Icon name="plus" size="sm" />
                        정답 추가
                      </LinkButton>
                    </div>
                  </div>
                </>
              )}
              {qType === "textarea" && (
                <OptionTextArea onChange={handleOption} readOnly={!editable} />
              )}
              {(qType === "radio" || qType === "checkbox") && (
                <OptionSelect
                  name={`q${props.n}_option_`}
                  type={qType}
                  list={options}
                  answers={answers}
                  error={error.a}
                  // readOnly={!editable}
                  onChange={handleChangeOption}
                  onRemove={handleRemoveOption}
                  onAdd={handleAddOptions}
                  onAnswer={handleAnswer}
                />
              )}
            </div>
          </div>
        </div>
      )}
      {editable && (
        <Button
          id="quiz_card_add_btn"
          type="primary"
          size="sm"
          className="self-stretch"
          onClick={handleAdd}
        >
          <Icon name="plus" size="sm" />
          추가
        </Button>
      )}
    </div>
  );
};

export default AddQuizCard;
